import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import { array, bool, number, object, string, instanceOf, element } from 'prop-types';
import { isEmpty } from 'lodash';
import { LazyComponent, useObserver } from '@fiverr-private/orca';
import { replaceUnderScoreWithHyphen } from '../../utils/replaceUnderScoreWithHyphen';
import { onDynamicImportError } from '../utils/errorHandling';
import { allocateToExperiments } from './utils';

const DEFAULT_FALLBACK = <></>;

export const PersonalizedComponentMapperCommon = (props) => {
    const { type, experiments_to_allocate: experimentsToAllocate } = props.component;
    const {
        shouldLazyImport,
        PersonalizedComponent,
        fallback = DEFAULT_FALLBACK,
        placeholder = DEFAULT_FALLBACK,
        funnelName,
        pageName,
        biParams,
    } = props;
    const personalizedClass = replaceUnderScoreWithHyphen(type);
    const observableRef = useRef(null);

    const onObserve = useCallback(() => {
        if (isEmpty(experimentsToAllocate)) {
            return;
        }

        allocateToExperiments(experimentsToAllocate, biParams);
    }, [experimentsToAllocate, biParams]);

    useObserver({ observableRef, onObserve });

    if (!PersonalizedComponent) {
        return null;
    }

    const onError = (error) => {
        const logMessage = `Failed to lazy load Component chunk: ${type}`;

        onDynamicImportError({ error, logMessage, funnelName, pageName });
    };

    return (
        <section ref={observableRef} className={classnames('recommendations-carousel', personalizedClass)}>
            {shouldLazyImport ? (
                <LazyComponent
                    lazyImport={PersonalizedComponent}
                    fallback={fallback}
                    placeholder={placeholder}
                    componentProps={props}
                    onError={onError}
                />
            ) : (
                <PersonalizedComponent {...props} />
            )}
        </section>
    );
};

export const componentMapperPropTypes = {
    isLazy: bool,
    isLoading: bool,
    sliderBaseNum: number,
    sliderBreakpoints: array,
    component: object.isRequired,
    sectionName: string.isRequired,
    mixpanelMetadata: object,
    collectProps: object,
    appName: string,
    fallback: instanceOf(element),
    biParams: object,
};

PersonalizedComponentMapperCommon.propTypes = {
    ...componentMapperPropTypes,
    shouldLazyImport: bool,
    PersonalizedComponentsMapper: instanceOf(element),
};
